import React from "react";
// Matarial imports
import Grid from "@mui/material/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import { ThemeProvider } from "@mui/material/styles";
import VerticalTab from "@limitless/modern/navigation/vertical-tabs/VerticalTabs";
import makeStyles from "@mui/styles/makeStyles";
// Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import { useExpertOnboardingContext } from "@limitless/expert";
import ExpertPracticeGroupSummaryList from "../components/onboarding/ExpertPracticeGroupSummaryList";
import OnboardingContainer from "../components/containers/onboarding-container/OnboardingContainer";
import { usePageViewRecorder } from "@limitless/analytics";
import Paper from "@limitless/modern/surfaces/paper/Paper";

import useCustomTheme from "../utils/hooks/useCustomTheme";
import Badge from "@limitless/modern/feedback/badge/Badge";

const useStyles = makeStyles(() => ({
    contentContainer: {
        height: "90vh",
        overflow: "auto",
        width: "calc(100% - 315px)"
    }
}));

const OnboardingScreen = () => {
    const { t } = useTranslation();
    usePageViewRecorder("Onboarding - Navigation");
    const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
    const { getOnboardingSummary, onboardingSummary } = useExpertOnboardingContext();
    const { loading, newInvitations, completed, inProgress, waitingList, hasNoInvitations } = onboardingSummary;
    const theme = useCustomTheme();
    const classes = useStyles();

    React.useEffect(() => {
        getOnboardingSummary();
    }, []);

    const onboardingContent = [
        newInvitations.length > 0 && (
            <ExpertPracticeGroupSummaryList
                title="Not started"
                groups={newInvitations}
                key="available"
                ctaType="start"
                isNotStartedTab={true}
            />
        ),
        inProgress.length > 0 && (
            <ExpertPracticeGroupSummaryList
                title="In Progress"
                groups={inProgress}
                key="inProgress"
                ctaType="continue"
                isNotStartedTab={false}
            />
        ),

        completed.length > 0 && (
            <ExpertPracticeGroupSummaryList
                title="Completed"
                groups={completed}
                key="completed"
                ctaType="view"
                isNotStartedTab={false}
            />
        ),
        waitingList.length > 0 && (
            <ExpertPracticeGroupSummaryList
                title="Waiting List"
                groups={waitingList}
                key="waiting"
                ctaType="waiting"
                isNotStartedTab={false}
            />
        )
    ].filter(Boolean);

    const onboardingStatus = [];
    if (newInvitations.length > 0) {
        onboardingStatus.push({
            label: (
                <Badge
                    name="available"
                    count={newInvitations.length}
                    content="Not started"
                    sx={{ "& .MuiBadge-badge": { left: 80, top: 5 } }}
                />
            ),
            value: "Available"
        });
    }
    if (inProgress.length > 0) {
        onboardingStatus.push({ label: "In progress", value: "in progress" });
    }
    if (completed.length > 0) {
        onboardingStatus.push({ label: "Completed", value: "Completed" });
    }
    if (waitingList.length > 0) {
        onboardingStatus.push({ label: "Waiting list", value: "Waiting list" });
    }

    // let noInvitations;

    const handleChange = (e, value) => {
        setSelectedTabIndex(value);
    };

    const renderTab = React.useMemo(() => {
        return onboardingContent[selectedTabIndex];
    }, [selectedTabIndex, newInvitations, completed, inProgress, waitingList]);

    return (
        <ThemeProvider theme={theme}>
            <OnboardingContainer loading={loading}>
                {hasNoInvitations ? (
                    <Grid item xs={8} md={6} sx={{ mt: 2 }}>
                        <Typography color="textSecondary">
                            {
                                "You do not currently have any onboarding invitations open. We will notify you if this changes, please look out for any notifications in this section in the future."
                            }
                        </Typography>
                    </Grid>
                ) : (
                    <Grid container direction="row" sx={{ backgroundColor: theme => theme.palette.background.default }}>
                        <Grid item>
                            <VerticalTab items={onboardingStatus} onChange={handleChange} value={selectedTabIndex} />
                        </Grid>
                        <Grid className={classes.contentContainer}>
                            <Paper
                                sx={{
                                    width: "100%",
                                    maxWidth: "1000px",
                                    padding: "40px 48px",
                                    paddingBottom: "5%"
                                }}
                            >
                                {renderTab}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </OnboardingContainer>
        </ThemeProvider>
    );
};

export default OnboardingScreen;
